import { useState } from "react";
import { AlertTriangle, UserMinus, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Closer } from "@/types/client";

interface CloserRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  closer: Closer | null;
  clientId: string;
  clientName: string;
}

export function CloserRequestModal({
  open,
  onOpenChange,
  closer,
  clientId,
  clientName,
}: CloserRequestModalProps) {
  const [requestType, setRequestType] = useState<'removal' | 'replacement'>('replacement');
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!closer) return;

    if (!reason.trim()) {
      toast.error('Reason Required', {
        description: 'Please provide a reason for your request.',
      });
      return;
    }

    if (reason.trim().length < 20) {
      toast.error('More Detail Needed', {
        description: 'Please provide more detail about your request (at least 20 characters).',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('closer_requests')
        .insert({
          client_id: clientId,
          closer_id: closer.id,
          request_type: requestType,
          reason: reason.trim(),
          status: 'pending',
        });

      if (error) throw error;

      toast.success('Request Submitted', {
        description: `Your ${requestType} request has been submitted. Our team will review and respond within 24 hours.`,
      });
      setReason("");
      setRequestType('replacement');
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting closer request:', error);
      toast.error('Submission Failed', {
        description: 'Could not submit your request. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!closer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-warning" />
            Request for {closer.name}
          </DialogTitle>
          <DialogDescription>
            Submit a request to remove or replace this closer from your account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Request Type */}
          <div className="space-y-3">
            <Label>Request Type</Label>
            <RadioGroup
              value={requestType}
              onValueChange={(value) => setRequestType(value as 'removal' | 'replacement')}
              className="grid grid-cols-2 gap-4"
            >
              <div className="relative">
                <RadioGroupItem
                  value="replacement"
                  id="replacement"
                  className="peer sr-only"
                />
                <Label
                  htmlFor="replacement"
                  className="flex flex-col items-center justify-center rounded-lg border-2 border-border bg-muted/50 p-4 hover:bg-muted cursor-pointer peer-data-[state=checked]:border-secondary peer-data-[state=checked]:bg-secondary/10 transition-colors"
                >
                  <UserPlus className="w-6 h-6 mb-2 text-secondary" />
                  <span className="font-medium">Replacement</span>
                  <span className="text-xs text-muted-foreground text-center mt-1">
                    Assign a new closer
                  </span>
                </Label>
              </div>
              <div className="relative">
                <RadioGroupItem
                  value="removal"
                  id="removal"
                  className="peer sr-only"
                />
                <Label
                  htmlFor="removal"
                  className="flex flex-col items-center justify-center rounded-lg border-2 border-border bg-muted/50 p-4 hover:bg-muted cursor-pointer peer-data-[state=checked]:border-destructive peer-data-[state=checked]:bg-destructive/10 transition-colors"
                >
                  <UserMinus className="w-6 h-6 mb-2 text-destructive" />
                  <span className="font-medium">Removal</span>
                  <span className="text-xs text-muted-foreground text-center mt-1">
                    Remove without replacing
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Closer Stats */}
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-sm font-medium mb-2">Current Performance</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Demos Completed:</span>{" "}
                <span className="font-medium">{closer.demosCompleted}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Conversion Rate:</span>{" "}
                <span className="font-medium">{closer.conversionRate}%</span>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>{" "}
                <span className={`font-medium ${closer.status === 'active' ? 'text-success' : 'text-destructive'}`}>
                  {closer.status}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Last Demo:</span>{" "}
                <span className="font-medium">{closer.lastDemoDate}</span>
              </div>
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">
              Reason for Request <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Please describe why you are requesting this change..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[100px] resize-none"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {reason.length}/500 characters
            </p>
          </div>

          {/* Notice */}
          <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
            <p className="text-xs text-warning">
              <strong>Note:</strong> Our team will review your request within 24 hours.
              {requestType === 'replacement'
                ? " We'll assign a new closer based on availability and your account needs."
                : " Removing a closer without replacement may affect your demo capacity."}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            variant={requestType === 'removal' ? 'destructive' : 'default'}
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
